import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useParams } from "wouter";
import { publicApi, ApiError } from "@/lib/api";
import { applyTenantLanguage } from "@/lib/i18n";

/**
 * Public tenant configuration returned from /api/public/:slug/tenant
 */
export interface TenantConfig {
  name: string;
  timezone: string | null;
  publicLogoUrl: string | null;
  publicPrimaryColor: string | null;
  publicSecondaryColor: string | null;
  termsAndConditionsUrl: string | null;
  termsAndConditionsText: string | null;
  cancellationPolicyText: string | null;
  cancellationHours: number | null;
  changeFee: string | null;
  refundPercentage: number | null;
  // Extended fields for landing page branding
  branding: {
    primaryColor: string;
    accentColor: string;
    logoUrl: string | null;
    tagline: string;
  };
  contact: {
    phoneUs: string;
    phoneMx: string;
    email: string;
    operatingHours: string;
  };
  social: {
    facebook: string | null;
    instagram: string | null;
  };
  companyDescription: string;
  /** tenants.default_language ('en' | 'es'): the site's language unless the passenger picks one. */
  defaultLanguage?: string | null;
}

interface TenantContextValue {
  tenant: TenantConfig | null;
  slug: string;
  isLoading: boolean;
  error: string | null;
  isNotFound: boolean;
}

const TenantContext = createContext<TenantContextValue | null>(null);

interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const params = useParams<{ slug: string }>();
  const slug = params?.slug || "";

  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isNotFound, setIsNotFound] = useState(false);

  useEffect(() => {
    if (!slug) {
      setIsLoading(false);
      return;
    }

    async function fetchTenant() {
      setIsLoading(true);
      setError(null);
      setIsNotFound(false);

      try {
        const data = await publicApi.get<TenantConfig>(
          `/api/public/${slug}/tenant`
        );
        applyTenantLanguage(data.defaultLanguage);
        setTenant(data);
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.status === 404) {
            setIsNotFound(true);
            setError("Tenant not found");
          } else {
            setError(err.message);
          }
        } else {
          setError("Failed to load tenant configuration");
        }
        console.error("Failed to fetch tenant:", err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchTenant();
  }, [slug]);

  return (
    <TenantContext.Provider
      value={{ tenant, slug, isLoading, error, isNotFound }}
    >
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Hook to access tenant context
 * @throws Error if used outside TenantProvider
 */
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
}

export default TenantContext;
